<template>
  <div class="aiw2-bottombar">
    <button v-if="showPrompts" class="bb-prompts" @click="$emit('view-prompts')">🔍 View prompts</button>

    <button v-if="showBack" class="bb-back" @click="$emit('back')">← Back</button>
    <span v-else-if="finalNote" class="bb-final">🔒 {{ finalNote }}</span>

    <div class="bb-credits" :class="{ 'no-pop': !hasBreakdown }">
      <span class="coin">$</span>
      <strong>{{ creditsText }}</strong>
      <span v-if="balanceValue != null" class="balance">Balance: {{ balanceValue }} credits</span>

      <div v-if="hasBreakdown" class="credits-pop">
        <h4>Breakdown</h4>
        <ul class="cp-list">
          <li v-for="(row, i) in estimate.breakdown" :key="i">
            <strong>{{ row.credits }} credit{{ row.credits === 1 ? '' : 's' }}:</strong> {{ row.label }}
            <ul v-if="row.items && row.items.length">
              <li v-for="(item, j) in row.items" :key="j">{{ item.label }}</li>
            </ul>
          </li>
        </ul>
        <div v-if="estimate.image_credits > 0" class="cp-note">Images are billed when you generate each one.<template v-if="ownKeyProvider === 'anthropic'"> Images still cost 1 credit each — an OpenAI image model generates them, even on your Claude key.</template></div>
        <div v-if="estimate.is_admin" class="wg-note">Admin account - no credits charged.</div>
        <div v-else-if="estimate.is_white_glove" class="wg-note">White-glove account - no credits charged.</div>
      </div>
    </div>

    <span v-if="blockingReason" class="bb-block">{{ blockingReason }}</span>

    <button
      v-if="!hideNext"
      class="bb-next"
      :disabled="ctaDisabled || busy"
      @click="$emit('next')"
    >
      <span v-if="busy" class="bb-spin"></span>
      {{ ctaLabel }}
    </button>
  </div>
</template>

<script>
// Persistent app-level action bar shared by every step (mirrors the design's
// .bottom-bar). Stateless: the parent supplies the per-step CTA/credits and
// handles @back / @next.
export default {
  name: "AiwV2BottomBar",
  props: {
    showBack: { type: Boolean, default: false },
    estimate: { type: Object, default: null },
    balance: { type: Number, default: null },
    creditsLabel: { type: String, default: "" },
    // Publish shows the same estimate breakdown as earlier steps, but framed as
    // credits already spent — flips "Credits required" to "Credits used".
    used: { type: Boolean, default: false },
    // Shown in place of the Back button once the article is final (Publish).
    finalNote: { type: String, default: "" },
    ctaLabel: { type: String, default: "Continue →" },
    ctaDisabled: { type: Boolean, default: false },
    blockingReason: { type: String, default: "" },
    busy: { type: Boolean, default: false },
    hideNext: { type: Boolean, default: false },
    // Admin-only: shows a left-anchored "View prompts" button that opens the
    // Prompt Inspector. The parent owns the modal + gating.
    showPrompts: { type: Boolean, default: false },
    // 'openai' | 'anthropic' when the run bills the user's own key — drives the
    // "images still cost 1 credit" note on Claude runs (no image API there).
    ownKeyProvider: { type: String, default: null },
  },
  computed: {
    // Prefer the live store balance; fall back to the estimate's balance.
    balanceValue() {
      if (this.balance != null) return this.balance;
      return this.estimate && this.estimate.balance != null ? this.estimate.balance : null;
    },
    hasBreakdown() {
      return !!(this.estimate && this.estimate.breakdown && this.estimate.breakdown.length);
    },
    creditsText() {
      if (this.estimate && this.estimate.total != null) {
        return `Credits ${this.used ? "used" : "required"}: ${this.estimate.total}`;
      }
      return this.creditsLabel || "";
    },
  },
};
</script>

<style scoped>
.aiw2-bottombar {
  position: sticky;
  bottom: 0;
  z-index: 15;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 18px;
  padding: 14px 28px;
  background: var(--surface, #fff);
  border-top: 1px solid var(--border, #e4e4e4);
  box-shadow: 0 -1px 0 rgba(15, 20, 25, 0.02), 0 -4px 14px rgba(15, 20, 25, 0.04);
  font-family: "Mulish", system-ui, sans-serif;
}
.bb-back {
  background: transparent;
  border: none;
  color: var(--text-3, #6b7b7e);
  padding: 9px 14px;
  border-radius: 7px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;
}
.bb-back:hover { color: var(--text, #052e33); background: var(--surface-2, #f7f7f7); }

/* Admin "View prompts": pinned far-left; the rest of the bar stays right-clustered. */
.bb-prompts {
  margin-right: auto;
  background: transparent;
  border: 1px solid var(--border, #e4e4e4);
  color: var(--text-3, #6b7b7e);
  padding: 8px 12px;
  border-radius: 7px;
  font-weight: 600;
  font-size: 12.5px;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;
}
.bb-prompts:hover { color: var(--text, #052e33); background: var(--surface-2, #f7f7f7); border-color: var(--text-3, #6b7b7e); }

/* Everything is right-clustered (design parity): Back · credits · Next. */
.bb-credits {
  display: flex; align-items: center; gap: 8px;
  font-size: 12.5px;
  color: var(--text-2, #014751);
  position: relative;
}
.bb-credits strong { color: var(--text, #052e33); font-weight: 700; }
.bb-credits .coin {
  width: 18px; height: 18px; border-radius: 50%;
  background: var(--pop-green, #25a97a); color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 10px; font-weight: 700;
}
.bb-credits .balance { color: var(--text-3, #6b7b7e); font-size: 11.5px; margin-left: 4px; }
.bb-credits.no-pop { cursor: default; }
.bb-credits:not(.no-pop) { cursor: default; }
.credits-pop {
  position: absolute;
  bottom: calc(100% + 16px);
  right: 0;
  width: 320px;
  background: #fff;
  border: 1px solid var(--border, #e4e4e4);
  border-radius: 14px;
  box-shadow: var(--shadow-lg, 0 10px 36px rgba(15, 20, 25, 0.08));
  padding: 18px 20px;
  text-align: left;
  opacity: 0; visibility: hidden;
  transform: translateY(6px);
  transition: opacity 0.18s ease, transform 0.18s ease, visibility 0.18s;
  z-index: 40;
}
.bb-credits:hover .credits-pop, .credits-pop:hover { opacity: 1; visibility: visible; transform: translateY(0); }
/* The assistant FAB (fixed bottom-right, z-index 1000) must not cover the
   credits popover. .credits-pop's own z-index is trapped inside this bar's
   sticky stacking context (z 15), so lift the bar itself — but only while
   the popover is open, to avoid painting the bar over modals/overlays. */
.aiw2-bottombar:has(.bb-credits:hover),
.aiw2-bottombar:has(.credits-pop:hover) { z-index: 1001; }
.credits-pop h4 { margin: 0 0 10px; font-size: 15px; font-weight: 800; color: var(--text, #052e33); }
.credits-pop .cp-list { margin: 0; padding-left: 18px; font-size: 13px; color: var(--text-2, #014751); line-height: 1.5; }
.credits-pop .cp-list > li { margin-bottom: 8px; }
.credits-pop .cp-list > li:last-child { margin-bottom: 0; }
.credits-pop .cp-list ul { margin: 4px 0 0; padding-left: 18px; color: var(--text-3, #6b7b7e); }
.credits-pop .cp-note { margin-top: 10px; color: var(--text-3, #6b7b7e); font-size: 12px; font-style: italic; }
.credits-pop .wg-note { margin-top: 10px; color: var(--pop-green, #25a97a); font-size: 12px; font-weight: 600; }
.bb-block { color: var(--pop-red, #ff3939); font-size: 12px; max-width: 320px; }
.bb-final { color: var(--text-3, #6b7b7e); font-size: 12.5px; font-weight: 600; display: inline-flex; align-items: center; gap: 5px; }
.bb-next {
  background: var(--pop-orange, #ff5700);
  color: #fff;
  border: none;
  padding: 9px 18px;
  border-radius: 7px;
  font-weight: 600;
  font-size: 13.5px;
  cursor: pointer;
  font-family: inherit;
  display: inline-flex; align-items: center; gap: 6px;
  box-shadow: 0 2px 6px rgba(255, 87, 0, 0.25);
  transition: all 0.15s;
}
.bb-next:hover { background: var(--pop-orange-hover, #e64e00); }
.bb-next:disabled {
  background: #dce0e6;
  color: var(--text-muted, #9aa6a8);
  box-shadow: none;
  cursor: not-allowed;
}
.bb-spin {
  width: 12px; height: 12px;
  border: 2px solid rgba(255, 255, 255, 0.5); border-top-color: #fff;
  border-radius: 50%; display: inline-block;
  animation: aiw2-bb-spin 0.7s linear infinite;
}
@keyframes aiw2-bb-spin { to { transform: rotate(360deg); } }
</style>
